import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PackageCheck } from 'lucide-react';
import { SupplierActiveFilter, type PurchaseReceiptSummary } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { apiErrorMessage } from '@/services/apiClient';
import { usePurchaseReceiptsQuery } from '../api/purchase-receipts.api';
import { useSuppliersQuery } from '../api/suppliers.api';

const PAGE_SIZE = 20;

/** Every delivery counted in, across the branches the viewer can reach. */
export function GoodsReceiptsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches } = useBranch();

  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = usePurchaseReceiptsQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    supplierId,
    branchId,
    dateFrom,
    dateTo,
  });

  const suppliers = useSuppliersQuery({ pageSize: 100, active: SupplierActiveFilter.ALL });

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const columns: Column<PurchaseReceiptSummary>[] = [
    {
      key: 'reference',
      header: t('purchasing.receipts.fields.reference'),
      primary: true,
      render: (receipt) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {receipt.reference}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {receipt.supplierName}
          </span>
        </span>
      ),
    },
    {
      key: 'order',
      header: t('purchasing.receipts.fields.purchaseOrder'),
      render: (receipt) => (
        <span className="text-muted font-mono text-sm" dir="ltr">
          {receipt.purchaseOrderReference}
        </span>
      ),
    },
    {
      key: 'branch',
      header: t('purchasing.orders.fields.branch'),
      render: (receipt) => <span className="text-muted text-sm">{receipt.branchName}</span>,
    },
    {
      key: 'receivedOn',
      header: t('purchasing.receipts.fields.receivedOn'),
      render: (receipt) => (
        <span className="text-ink" dir="ltr">
          {receipt.receivedOn}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('purchasing.receipts.fields.quantity'),
      align: 'end',
      render: (receipt) => (
        <span className="text-ink" dir="ltr">
          {receipt.totalQuantity}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (receipt) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/receipts/${receipt.id}`)}
          aria-label={t('purchasing.receipts.openNamed', { reference: receipt.reference })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('purchasing.receipts.title')}
        subtitle={t('purchasing.receipts.subtitle')}
      />

      <Card>
        <CardHeader
          title={t('purchasing.receipts.title')}
          subtitle={
            data ? t('purchasing.receipts.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => onFilter(setSearch)(event.target.value)}
            placeholder={t('purchasing.receipts.searchPlaceholder')}
            aria-label={t('purchasing.receipts.searchLabel')}
          />
          <Select
            options={[
              { value: '', label: t('purchasing.orders.filters.allSuppliers') },
              ...(suppliers.data?.items ?? []).map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
              })),
            ]}
            value={supplierId}
            aria-label={t('purchasing.orders.fields.supplier')}
            onChange={(event) => onFilter(setSupplierId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('purchasing.orders.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('purchasing.orders.fields.branch')}
            onChange={(event) => onFilter(setBranchId)(event.target.value)}
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => onFilter(setDateFrom)(event.target.value)}
            aria-label={t('purchasing.orders.filters.dateFrom')}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => onFilter(setDateTo)(event.target.value)}
            aria-label={t('purchasing.orders.filters.dateTo')}
          />
        </div>

        <DataTable
          caption={t('purchasing.receipts.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(receipt) => receipt.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('purchasing.receipts.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={PackageCheck}
          emptyTitle={t('purchasing.receipts.empty')}
          emptyDescription={t('purchasing.receipts.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
